import { Injectable } from '@angular/core';
import { AngularFirestore, Query, QueryFn } from '@angular/fire/compat/firestore';
import { PermissionModel } from 'src/app/models/permission';
import { classToPlain, plainToClass } from 'class-transformer';
import { UserModel } from 'src/app/models/user';

import {
  ConversationMessageModel,
  HLConversationModel,
  HLTemplateModel,
  HLCorp,
  HLConversationStatus,
  HLConversationNotes,
  HLConversationAgent,
  IFirebaseFilterCriteria,
} from 'src/app/models/conversations';
import { BehaviorSubject, Observable, Subscription, firstValueFrom, map, combineLatestWith, defer, switchMap, distinctUntilChanged, filter } from 'rxjs';
import { RoleModel } from 'src/app/models';
import { Permissions } from 'src/app/utils/permissions/permissions';
import { buildAppointmentNoteResult, APPOINTMENT_NOTE_SYSTEM_USER } from 'src/app/modules/firebase/utils';
import { AuthService } from '../auth.service';
import * as firestore from 'firebase/firestore';
import Timestamp = firestore.Timestamp;
import firebase from 'firebase/compat';

// TODO Source me

@Injectable({
  providedIn: 'root',
})
export class HumanInLoopService {
  constructor(private afs: AngularFirestore, private authService: AuthService) {}

  private _conversationLoading = new BehaviorSubject(false);
  private _conversationData = new BehaviorSubject<any[]>([]);
  private _conversationCount = new BehaviorSubject(0);
  private _lastConversationCursor;

  private _messagesLoading = new BehaviorSubject(false);
  private _messagesData = new BehaviorSubject<ConversationMessageModel[]>([]);
  private _messagesDone = new BehaviorSubject(false);
  private _hasNewMessage = new BehaviorSubject(false);
  private _lastMessagesCursor;

  private _notesLoading = new BehaviorSubject(false);
  private _notesData = new BehaviorSubject<any[]>([]);
  private _notesDone = new BehaviorSubject(false);
  private _lastNoteCursor;

  private CONVERSATION_ORDER_FIELD = 'lastUpdated';
  private LAST_MESSAGE_DATE = 'lastMessageDate';
  private MESSAGE_ORDER_FIELD = 'createdAt';
  private NOTES_ORDER_FIELD = 'createdAt';
  private CONVERSATIONS_LIMIT = 150;
  private MESSAGES_LIMIT = 50;
  private NOTES_LIMIT = 50;
  private conversationSubscription: Subscription | null = null;

  conversationData: Observable<any> = this._conversationData.asObservable();
  conversationCount: Observable<number> = this._conversationCount.asObservable();

  messagesLoading: Observable<boolean> = this._messagesLoading.asObservable();
  messagesData: Observable<ConversationMessageModel[]> = this._messagesData.asObservable();
  messagesDone: Observable<any> = this._messagesDone.asObservable();
  hasNewMessages: Observable<boolean> = this._hasNewMessage.asObservable();

  notesLoading: Observable<boolean> = this._notesLoading.asObservable();
  notesData: Observable<any> = this._notesData.asObservable();
  notesDone: Observable<any> = this._notesDone.asObservable();

  messagesSubscription: Subscription;
  loadMoreMessagesSubscription: Subscription;
  private notesSubscription: Subscription | null = null;
  private loadMoreNotesSubscription: Subscription | null = null;

  // This new function processes the user query in batches of 10 (firebase limitation)
  async getUsersInBatch(userIds: string[], currentUser: UserModel | null) {
    const users: UserModel[] = [];

    // we can only query in batches of 10 - so loop through the userIds and process 10 at a time.

    for (let i = 0; i < userIds.length; i += 10) {
      const userBatch = userIds.slice(i, i + 10);
      const usersCollection = await this.afs.collection<UserModel>('users', ref => ref.where('id', 'in', userBatch));
      await firstValueFrom(usersCollection.get()).then(usersSnapshots => {
        // tslint:disable-next-line:prefer-for-of no-non-null-assertion
        for (let j = 0; j < usersSnapshots!.docs.length; j++) {
          const user = usersSnapshots.docs[j].data() as UserModel;
          // only add the user to list if they are part of the client's team or you!
          if ((user.role !== 'admin' && !user.role.startsWith('carlabs')) || (currentUser !== null && user.id === currentUser.id)) {
            user.id = usersSnapshots.docs[j].id;
            users.push(user);
          }
        }
      });
    }
    users.sort((a, b) => {
      const nameA = a.firstName?.toLowerCase() || '';
      const nameB = b.firstName?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
    return users;
  }

  async getBotUsers(corpId, botCode, currentUser: UserModel | null) {
    const queryFn: QueryFn = ref => {
      let q: firebase.firestore.Query<firebase.firestore.DocumentData> = ref;
      if (corpId) {
        q = q.where('corpId', '==', corpId);
      }
      if (botCode) {
        q = q.where('botCode', '==', botCode);
      }
      return q;
    };
    const permissionsCollection = this.afs.collection<PermissionModel>('permissions', queryFn);
    const snapshots = await firstValueFrom(permissionsCollection.get());
    const userIds: string[] = [];
    snapshots.docs.forEach(row => {
      const userId = row.get('userId');
      if (!userIds.includes(userId)) {
        userIds.push(userId);
      }
    });
    // This new function processes the user query in batches of 10 (firebase limitation)
    const users = await this.getUsersInBatch(userIds, currentUser);
    return users;
  }

  getCorpConversationsCollection(corpId) {
    const conversationsRef = this.afs.collection('conversations').doc(corpId).collection<HLConversationModel>('conversations');
    return conversationsRef;
  }

  getCorpHLTemplatesCollection(corpId: string, queryFn?: QueryFn) {
    const ref = this.afs.collection('conversations').doc(corpId).collection('human_templates', queryFn);
    return ref;
  }

  getCorpConversationMessageCollection(corpId: string, conversationId: string) {
    const conversationsRef = this.getCorpConversationsCollection(corpId);
    const messagesRef = conversationsRef.doc(conversationId).collection<ConversationMessageModel>('messages', ref => ref.orderBy('createdAt', 'asc'));
    return messagesRef;
  }

  async touchConversation(corpId: string, conversationId: string): Promise<void> {
    await this.getCorpConversationsCollection(corpId).doc(conversationId).update({ touched: true });
  }

  getCorpTags(corpId) {
    return this.afs.collection('conversations').doc<HLCorp>(corpId).valueChanges();
  }

  saveCorpTags(corpId: string, corp: HLCorp): Promise<void> {
    return this.afs.collection('conversations').doc(corpId).set({ conversation_tags: corp.conversation_tags }, { merge: true });
  }

  getCorpHLTemplates(corpId: string): Observable<HLTemplateModel[]> {
    return this.getCorpHLTemplatesCollection(corpId, ref => ref.orderBy('name'))
      .valueChanges({ idField: 'id' })
      .pipe(
        map(rows =>
          rows.map(row => {
            const template = row as HLTemplateModel;
            template.id = row.id;
            return template;
          }),
        ),
      );
  }

  deleteCorpHLTemplate(corpId: string, template: HLTemplateModel) {
    return this.getCorpHLTemplatesCollection(corpId).doc(template.id).delete();
  }

  createCorpHLTemplate(corpId: string, template: HLTemplateModel) {
    if (!template.tags) {
      template.tags = [];
    }

    return this.getCorpHLTemplatesCollection(corpId)
      .doc(template.id)
      .set(Object.assign({}, classToPlain(template)));
  }

  getHILSupportedRoles() {
    const rolesRef = this.afs.collection<RoleModel>('roles', ref => ref.where('permissions', 'array-contains', Permissions.CAN_HANDLE_BOT_SUPPORT));
    return rolesRef.valueChanges({ idField: 'id' }).pipe(
      map(rows =>
        rows.map(row => {
          return plainToClass(RoleModel, row);
        }),
      ),
    );
  }

  /**
   * Checks if current user should be whitelisted from triggering HIL status changes
   * Internal users (monitoring, QA, admin roles) should not change conversation status when viewing
   */
  async isWhitelistedFromHILStatusChange(): Promise<boolean> {
    const currentUser = await this.authService.getCurrentUserProfile();
    if (!currentUser) {
      return false;
    }

    // Define whitelisted roles based on existing roles in database
    const whitelistedRoles = ['admin', 'CarLabs Sales Leadership', 'professional_services', 'qa_monitoring'];

    // Check if user's role is whitelisted
    return whitelistedRoles.includes(currentUser.role);
  }

  async removerAgentFromConversation(corpId: string, user: UserModel, conversationId: string) {
    const snapshot = await firstValueFrom(
      this.afs.collection('conversations').doc(corpId).collection<HLConversationModel>('conversations').doc(conversationId).get(),
    );
    const doc = snapshot.data();

    let assignments: HLConversationAgent[] = doc?.assignments || [];

    if (doc?.botId) {
      if (doc.assignments && doc.assignments.length > 0) {
        doc.assignments = doc.assignments.filter(a => a.agentId !== user.id);
      } else {
        doc.assignments = [];
      }

      if (doc.assignmentUserIds && doc.assignmentUserIds.length > 0) {
        doc.assignmentUserIds = doc.assignmentUserIds.filter(a => a !== user.id);
      } else {
        doc.assignmentUserIds = [];
      }

      if (doc.assignmentCount) {
        doc.assignmentCount = doc.assignmentCount - 1;
      }

      this.afs.collection('conversations').doc(corpId).collection<HLConversationModel>('conversations').doc(conversationId).set(doc);

      assignments = doc.assignments || [];
    }
    return assignments;
  }

  async saveConversationTags(corpId: string, conversationId: string, tags: string[]) {
    this.afs.collection('conversations').doc(corpId).collection('conversations').doc(conversationId).set({ tags }, { merge: true });
    return tags;
  }

  async addAgentToConversation(corpId: string, user: UserModel, conversationId: string) {
    const snapshot = await firstValueFrom(
      this.afs.collection('conversations').doc(corpId).collection<HLConversationModel>('conversations').doc(conversationId).get(),
    );
    const doc = snapshot.data();

    let assignments: HLConversationAgent[] = [];
    if (doc?.botId) {
      const newUser = {
        agentAvatarUrl: user.avatar || '',
        agentId: user.id,
        agentName: user.fullName ? user.fullName : `${user.firstName} ${user.lastName}`,
      };

      if (doc.assignments && doc.assignments.length > 0) {
        doc.assignments?.push(newUser);
      } else {
        doc.assignments = [newUser];
      }

      if (doc.assignmentUserIds && doc.assignmentUserIds.length > 0) {
        doc.assignmentUserIds.push(user.id);
      } else {
        doc.assignmentUserIds = [user.id];
      }

      if (doc.assignmentCount) {
        doc.assignmentCount = doc.assignmentCount + 1;
      }

      this.afs.collection('conversations').doc(corpId).collection<HLConversationModel>('conversations').doc(conversationId).set(doc);
      assignments = doc.assignments || [];
    }
    return assignments;
  }

  async updateConversationReadStatus(corpId: string, conversationId: string, isRead: boolean): Promise<void> {
    return await this.afs
      .collection('conversations')
      .doc(corpId)
      .collection<HLConversationModel>('conversations')
      .doc(conversationId)
      .update({ status: isRead ? HLConversationStatus.Read : HLConversationStatus.Unread });
  }

  clearData(collection: 'conversations' | 'messages' | 'notes'): void {
    let data;
    let doneState;
    switch (collection) {
      case 'conversations':
        this.unsubscribeFromConversations();
        this._lastConversationCursor = null;
        this._conversationCount.next(0);
        this._conversationLoading.next(false);
        data = this._conversationData;
        break;

      case 'messages':
        data = this._messagesData;
        doneState = this._messagesDone;
        this._hasNewMessage.next(false);
        break;

      case 'notes':
        data = this._notesData;
        doneState = this._notesDone;
        // Unsubscribe from any active notes listeners
        if (this.notesSubscription) {
          this.notesSubscription.unsubscribe();
          this.notesSubscription = null;
        }
        if (this.loadMoreNotesSubscription) {
          this.loadMoreNotesSubscription.unsubscribe();
          this.loadMoreNotesSubscription = null;
        }
        this._lastNoteCursor = null;
        break;

      default:
        break;
    }
    doneState?.next(false);
    data.next([]);
  }

  buildQuery(ref: Query, whereClauses: IFirebaseFilterCriteria[]): Query {
    return whereClauses.reduce((query, c) => {
      if (c) {
        return query.where(c.field, c.operator, c.value);
      } else {
        return query;
      }
    }, ref);
  }

  /**
   * Build firebase query to load conversations based on the level (i.e corp, hierarchy or bot)
   * NOTE: firebase requires index on these fields for the query to work
   * Provisional filtering is handled client-side to avoid Firestore inequality constraints
   * @param ref - Firebase collection ref
   * @param limit - number of records to limit result to
   * @param corpId - corp Id
   * @param hierarchy - hierarchy level
   * @param botCode - bot code
   * @param environment - environment
   * @param excludeProvisional - whether to exclude Voice AI data via client-side filtering (default: true)
   */
  buildConversationQuery(
    ref: Query,
    limit: number,
    corpId: string,
    hierarchy: string,
    botCode: string,
    environment: string,
    filters: IFirebaseFilterCriteria[] = [],
  ): Query {
    const _filters: IFirebaseFilterCriteria[] = [...filters];
    if (botCode && botCode.length > 0) {
      // filter only the given bot conversations
      const botFullId = `${corpId}-${hierarchy}-${botCode}_${environment}`;
      _filters.push({
        field: 'botId',
        operator: '==',
        value: botFullId,
      });
    } else if (hierarchy && hierarchy.length > 0) {
      // filter only conversation under the given hierarchy
      _filters.push({
        field: 'hierarchyElements',
        operator: 'array-contains',
        value: hierarchy,
      });
    }

    _filters.push({
      field: 'hadFirstCustomerEngagement',
      operator: '==',
      value: true,
    });

    const queryRef = this.buildQuery(ref, _filters);
    return queryRef.orderBy(this.CONVERSATION_ORDER_FIELD, 'desc').limit(limit);
  }

  /**
   * Function to filter out provisional Voice AI data
   * @param conversations - array of conversations to filter
   * @returns filtered array excluding Voice AI conversations (campaignCode='VA')
   */
  private filterProvisionalData(conversations: any[]): any[] {
    return conversations.filter(conversation => conversation && conversation.campaign !== 'VA');
  }

  async loadInitialConversation(corpId: string, conversationId: string) {
    const CONVERSATIONS_PATH = `/conversations/${corpId}/conversations`;
    const conversation = await firstValueFrom(this.afs.collection<HLConversationModel>(CONVERSATIONS_PATH).doc(conversationId).get());

    const lastMessageDate = conversation[this.LAST_MESSAGE_DATE] || conversation[this.CONVERSATION_ORDER_FIELD];

    if (conversation) {
      return { ...conversation.data(), firebaseDocumentId: conversation.id, lastMessageDate };
    }
    return null;
  }

  /**
   * Get a conversation from the list of conversations already loaded in memory
   * Note: this function does not load from firebase
   */
  getConversationsFromMemoryById(conversationId: string) {
    const foundItem = this._conversationData.value.filter(c => c.firebaseDocumentId === conversationId);
    if (foundItem.length > 0) {
      return foundItem[0] as HLConversationModel;
    }
  }

  // tslint:disable-next-line: no-shadowed-variable
  listenToConversations<HLConversationModel>(
    corpId: string,
    filters: IFirebaseFilterCriteria[] = [],
    limit: number = this.CONVERSATIONS_LIMIT,
    hierarchy: string = '',
    botCode: string = '',
    environment: string = 'development',
    excludeProvisional: boolean = false,
  ) {
    const CONVERSATIONS_PATH = `/conversations/${corpId}/conversations`;
    this.unsubscribeFromConversations();

    // If the previous count was greater than the default limit
    // we use that limit. else, we show the default limit.
    const limitToUse = limit > this.CONVERSATIONS_LIMIT ? limit : this.CONVERSATIONS_LIMIT;

    // console.log('listenToConversations -> limitToUse',limitToUse);
    // console.log('listenToConversations -> hierarchy',hierarchy);
    // console.log('listenToConversations -> botCode',botCode);
    this.conversationSubscription = this.afs
      .collection<HLConversationModel>(CONVERSATIONS_PATH, ref => {
        return this.buildConversationQuery(ref, limitToUse, corpId, hierarchy, botCode, environment, filters);
      })
      .snapshotChanges()
      .subscribe({
        next: convoSnapshot => {
          Promise.all(
            convoSnapshot.map(convo => {
              const conv = convo.payload.doc.data();
              const lastMessageDate = conv[this.LAST_MESSAGE_DATE] || conv[this.CONVERSATION_ORDER_FIELD];
              return {
                ...convo.payload.doc.data(),
                firebaseDocumentId: convo.payload.doc.id,
                lastMessageDate,
              };
            }),
          )
            .then(data => {
              // Apply filtering for Voice AI data if excludeProvisional is true
              if (excludeProvisional) {
                data = this.filterProvisionalData(data);
              }

              // Firebase orderBy handles sorting - no local sorting needed
              this._conversationData.next(data);
              this._conversationCount.next(data.length);

              if (this._lastConversationCursor && convoSnapshot.length > 0) {
                console.log('Get Next Cursor', this._lastConversationCursor.id, ' => ', convoSnapshot[convoSnapshot.length - 1].payload.doc.id);
              }
              if (convoSnapshot.length > 0 && this._lastConversationCursor !== convoSnapshot[convoSnapshot.length - 1].payload.doc.id) {
                this._lastConversationCursor = this.getCursor(convoSnapshot);
                console.log('Done Setting Next Cursor', this._lastConversationCursor.id, ' => ', convoSnapshot[convoSnapshot.length - 1].payload.doc.id);
              }
            })
            .catch(error => console.log(error));
        },
        error: error => {
          console.error('Error: ', error);
        },
      });
  }

  unsubscribeFromConversations() {
    if (this.conversationSubscription) {
      this.conversationSubscription.unsubscribe();
      this.conversationSubscription = null;
    }
  }

  // tslint:disable-next-line: no-shadowed-variable
  loadMoreConversations<HLConversationModel>(
    corpId: string,
    hierarchy: string = '',
    botCode: string = '',
    environment: string = 'development',
    excludeProvisional: boolean = false,
  ): void {
    if (this._conversationLoading.value === true) {
      return;
    }

    console.log('conversationScrollListener - started', corpId, hierarchy, botCode, new Date());
    const CONVERSATIONS_PATH = `/conversations/${corpId}/conversations`;
    this._conversationLoading.next(true);

    this.afs
      .collection<HLConversationModel>(CONVERSATIONS_PATH, ref => {
        let q = this.buildConversationQuery(ref, this.CONVERSATIONS_LIMIT, corpId, hierarchy, botCode, environment, []);
        if (this._lastConversationCursor) {
          q = q.startAfter(this._lastConversationCursor);
        }
        return q;
      })
      .get()
      .subscribe({
        next: convoSnapshot => {
          let data = [...this._conversationData.value];
          const indexById = new Map<string, number>(data.map((c: any, i: number) => [c.firebaseDocumentId, i]));

          for (const doc of convoSnapshot.docs) {
            const id = doc.id;
            const conv: any = doc.data();
            const lastMessageDate = conv[this.LAST_MESSAGE_DATE] || conv[this.CONVERSATION_ORDER_FIELD];
            const nextItem = {
              ...conv,
              firebaseDocumentId: id,
              lastMessageDate,
            };

            const idx = indexById.get(id);
            if (idx !== undefined) {
              data[idx] = nextItem;
            } else {
              indexById.set(id, data.length);
              data.push(nextItem);
            }
          }

          if (excludeProvisional) {
            data = this.filterProvisionalData(data);
          }

          this._conversationData.next(data);
          this._conversationCount.next(data.length);
          if (convoSnapshot.docs.length > 0) {
            this._lastConversationCursor = convoSnapshot.docs[convoSnapshot.docs.length - 1];
          }
          this._conversationLoading.next(false);

          console.log('conversationScrollListener - done', corpId, hierarchy, botCode, new Date());
        },
        error: error => {
          console.log(error);
          this._conversationLoading.next(false);
        },
      });
  }

  listenToMessages(corpId: string, conversationId: string) {
    try {
      const MESSAGES_PATH = `/conversations/${corpId}/conversations/${conversationId}/messages`;

      if (this.messagesSubscription) {
        this.messagesSubscription.unsubscribe();
      }
      this.messagesSubscription = this.afs
        .collection<ConversationMessageModel>(MESSAGES_PATH, ref => {
          return ref.orderBy(this.MESSAGE_ORDER_FIELD, 'desc').limit(this.MESSAGES_LIMIT);
        })
        .snapshotChanges()
        .subscribe(messageSnapshot => {
          const data = messageSnapshot
            .map(msg => {
              const messageData: ConversationMessageModel = {
                ...msg.payload.doc.data(),
                firebaseDocumentId: msg.payload.doc.id,
              };
              return ConversationMessageModel.addSetNodeLogMessage(messageData);
            })
            .reverse();
          this._messagesData.next(data);
          this._hasNewMessage.next(true);
          this._lastMessagesCursor = this.getCursor(messageSnapshot);
        });
    } catch (error) {
      console.error(error);
    }
  }

  loadMoreMessages(corpId: string, conversationId: string): void {
    if (this._messagesDone.value || this._messagesLoading.value) {
      return;
    }

    const MESSAGES_PATH = `/conversations/${corpId}/conversations/${conversationId}/messages`;
    this._messagesLoading.next(true);

    if (this.loadMoreMessagesSubscription) {
      this.loadMoreMessagesSubscription.unsubscribe();
    }
    this.loadMoreMessagesSubscription = this.afs
      .collection<ConversationMessageModel>(MESSAGES_PATH, ref => {
        return ref.orderBy(this.MESSAGE_ORDER_FIELD, 'desc').limit(this.MESSAGES_LIMIT).startAfter(this._lastMessagesCursor);
      })
      .snapshotChanges()
      .subscribe(msgSnapshot => {
        if (msgSnapshot.length < this.MESSAGES_LIMIT) {
          this._messagesDone.next(true);
        }
        const data = [...this._messagesData.value];
        msgSnapshot.map(msg => {
          const messageData: ConversationMessageModel = {
            ...msg.payload.doc.data(),
            firebaseDocumentId: msg.payload.doc.id,
          };
          data.unshift(ConversationMessageModel.addSetNodeLogMessage(messageData));
        });

        this._messagesData.next(data);
        this._lastMessagesCursor = this.getCursor(msgSnapshot);
        this._messagesLoading.next(false);
      });
  }

  addSetNodeMessage(corpId: string, conversationId: string, messageText: string, agentName: string): Promise<any> {
    const message = new ConversationMessageModel();
    message.agentName = agentName;
    message.messageType = 'setNode';
    message.channel = 'web';
    message.nodeName = messageText;
    message.createdAt = Timestamp.now();

    const str = JSON.stringify(message, function replacer(key, value) {
      if (this && key === 'firebaseDocumentId') {
        return undefined;
      }
      return value;
    });

    const cleanMessage = JSON.parse(str);

    return this.afs
      .collection('conversations')
      .doc(corpId)
      .collection<HLConversationModel>('conversations')
      .doc(conversationId)
      .collection<ConversationMessageModel>('messages')
      .add(cleanMessage);
  }

  addConversationNote(corpId: string, conversationId: string, note: string, user: HLConversationAgent): Promise<any> {
    return this.afs
      .collection('conversations')
      .doc(corpId)
      .collection<HLConversationModel>('conversations')
      .doc(conversationId)
      .collection<HLConversationNotes>('notes')
      .add({
        note,
        createdAt: firestore.serverTimestamp(),
        user,
      });
  }

  updateConversationNote(corpId: string, conversationId: string, noteId: string, note: string): Promise<any> {
    return this.afs
      .collection('conversations')
      .doc(corpId)
      .collection<HLConversationModel>('conversations')
      .doc(conversationId)
      .collection<HLConversationNotes>('notes')
      .doc(noteId)
      .update({ note });
  }

  deleteConversationNote(corpId: string, conversationId: string, noteId: string): Promise<any> {
    return this.afs
      .collection('conversations')
      .doc(corpId)
      .collection<HLConversationModel>('conversations')
      .doc(conversationId)
      .collection<HLConversationNotes>('notes')
      .doc(noteId)
      .delete();
  }

  // tslint:disable-next-line: no-shadowed-variable
  listenToNotes<HLConversationNotes>(corpId: string, conversationId: string, limit: number = this.NOTES_LIMIT) {
    const conversationsCollection = this.getCorpConversationsCollection(corpId);

    // Unsubscribe from any previous notes listeners
    if (this.notesSubscription) {
      this.notesSubscription.unsubscribe();
      this.notesSubscription = null;
    }
    if (this.loadMoreNotesSubscription) {
      this.loadMoreNotesSubscription.unsubscribe();
      this.loadMoreNotesSubscription = null;
    }
    // Reset cursor for new conversation
    this._lastNoteCursor = null;

    const notes$ = conversationsCollection
      .doc(conversationId)
      .collection<HLConversationNotes>('notes', ref => ref.orderBy(this.NOTES_ORDER_FIELD, 'desc').limit(limit))
      .snapshotChanges()
      .pipe(
        map(notesSnapshot => {
          this._lastNoteCursor = this.getCursor(notesSnapshot);
          return notesSnapshot.map(note => {
            return { ...note.payload.doc.data(), firebaseDocumentId: note.payload.doc.id };
          });
        }),
      );

    const conversation$ = conversationsCollection.doc<HLConversationModel>(conversationId).valueChanges();

    const messages$ = defer(() => this.getCorpConversationMessageCollection(corpId, conversationId).valueChanges());

    const appointmentNote$ = conversation$.pipe(
      filter((conversation): conversation is HLConversationModel => !!conversation),
      map(({ appointmentCreationTime, scheduledAppointmentType, unscheduledAppointmentType }) => ({
        scheduledAppointmentType,
        unscheduledAppointmentType,
        appointmentCreationTime,
      })),
      distinctUntilChanged(
        (a, b) =>
          a.scheduledAppointmentType === b.scheduledAppointmentType &&
          a.unscheduledAppointmentType === b.unscheduledAppointmentType &&
          a.appointmentCreationTime?.toMillis() === b.appointmentCreationTime?.toMillis(),
      ),
      switchMap(conversation => defer(() => this.buildAppointmentDetailsNote(conversation, messages$))),
    );

    this.notesSubscription = notes$.pipe(combineLatestWith(appointmentNote$)).subscribe(async ([notes, appointmentNote]) => {
      const allNotes = appointmentNote ? [...notes, appointmentNote] : notes;
      const sortedNotes = allNotes.sort((a: any, b: any) => b.createdAt?.toMillis() - a.createdAt?.toMillis());

      this._notesData.next(sortedNotes);
    });
  }

  private async buildAppointmentDetailsNote(
    conversation: Pick<HLConversationModel, 'appointmentCreationTime' | 'scheduledAppointmentType' | 'unscheduledAppointmentType'>,
    messages$: Observable<ConversationMessageModel[]>,
  ): Promise<HLConversationNotes | null> {
    if (!conversation) {
      return null;
    }

    // Extract appointment data from conversation (these properties may exist on Firebase data but aren't typed in HLConversationModel)
    const appointmentData = {
      scheduledAppointmentType: conversation.scheduledAppointmentType,
      unscheduledAppointmentType: conversation.unscheduledAppointmentType,
      appointmentCreationTime: conversation.appointmentCreationTime,
    };

    const result = await buildAppointmentNoteResult(appointmentData, messages$);

    if (!result) {
      return null;
    }

    return {
      createdAt: result.createdAt,
      note: result.noteHtml,
      user: APPOINTMENT_NOTE_SYSTEM_USER,
    } as HLConversationNotes;
  }

  // tslint:disable-next-line: no-shadowed-variable
  loadMoreNotes<HLConversationNotes>(corpId: string, conversationId: string): void {
    if (this._notesDone.value || this._notesLoading.value) {
      return;
    }

    const NOTES_PATH = `/conversations/${corpId}/conversations/${conversationId}/notes`;
    this._notesLoading.next(true);

    if (this.loadMoreNotesSubscription) {
      this.loadMoreNotesSubscription.unsubscribe();
    }
    this.loadMoreNotesSubscription = this.afs
      .collection<HLConversationNotes>(NOTES_PATH, ref => {
        return ref.orderBy(this.NOTES_ORDER_FIELD, 'desc').limit(this.NOTES_LIMIT).startAfter(this._lastNoteCursor);
      })
      .snapshotChanges()
      .subscribe(notesSnapshot => {
        if (notesSnapshot.length < this.NOTES_LIMIT) {
          this._notesDone.next(true);
        }
        const data = [...this._notesData.value];
        notesSnapshot.map(note => {
          data.push({ ...note.payload.doc.data(), firebaseDocumentId: note.payload.doc.id });
        });

        this._notesData.next(data);
        this._lastNoteCursor = this.getCursor(notesSnapshot);
        this._notesLoading.next(false);
      });
  }

  private getCursor(value) {
    if (value.length) {
      return value[value.length - 1].payload.doc;
    }
    return null;
  }

  async getLastUserSentMessageInConversation(corpId: string, conversationId: string): Promise<ConversationMessageModel | undefined> {
    const MESSAGES_PATH = `/conversations/${corpId}/conversations/${conversationId}/messages`;
    try {
      const customerMessages = await this.afs.collection<ConversationMessageModel>(MESSAGES_PATH, ref =>
        ref.orderBy('createdAt', 'desc').where('userType', '==', 'customer'),
      );
      const customerMessageSnapshot = await firstValueFrom(customerMessages.get());

      // tslint:disable-next-line:no-non-null-assertion
      const customerMessagesArray = customerMessageSnapshot!.docs.map(doc => {
        const message = doc.data() as ConversationMessageModel;
        return message;
      });

      if (customerMessagesArray.length) {
        return customerMessagesArray[0];
      }

      return undefined;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Finds the most recent message in the conversation that has an `emailSubject`,
   * regardless of `userType` or `channel`. Useful for pre-filling the reply
   * subject line when the most recent message wasn't an inbound customer email
   * (e.g. mixed SMS/email threads, or outbound-only conversations).
   */
  async getLastEmailSubjectInConversation(corpId: string, conversationId: string): Promise<string | undefined> {
    const MESSAGES_PATH = `/conversations/${corpId}/conversations/${conversationId}/messages`;
    try {
      const messages = this.afs.collection<ConversationMessageModel>(MESSAGES_PATH, ref => ref.orderBy('createdAt', 'desc').limit(50));
      const snapshot = await firstValueFrom(messages.get());
      // tslint:disable-next-line:no-non-null-assertion
      const docs = snapshot!.docs;
      for (const doc of docs) {
        const data = doc.data() as ConversationMessageModel;
        const subject = data?.emailSubject?.trim();
        if (subject) {
          return subject;
        }
      }
      return undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}
